/**
 * Custom hook for managing Pusher connections in the debate room
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { usePusherChannel, usePusherEvent } from './usePusher';
import { useDebate } from '../contexts/DebateContext';
import { useAuth } from '../contexts/AuthContext';
import { sendPusherMessage, getDebateChannelName, getUserChannelName } from '../services/pusherService';
import { toast } from 'react-hot-toast';
import logger from '../utils/logger';

// Safe logger utility to prevent build errors
const safeLogger = {
  info: logger?.info || console.log,
  error: logger?.error || console.error,
  warn: logger?.warn || console.warn,
  debug: logger?.debug || console.log
};

/**
 * Custom hook for using Pusher in the debate room
 * @param {string} debateId - The ID of the debate
 * @param {string} teamId - The ID of the user's team
 * @returns {Object} An object containing Pusher-related functionality for the debate room
 */
const usePusherForDebateRoom = (debateId, teamId) => {
  const { currentDebate, setCurrentDebate, fetchDebateById, startFallbackPolling } = useDebate();
  const { user } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const [typingUsers, setTypingUsers] = useState([]);
  const debateChannelRef = useRef(null);
  const userChannelRef = useRef(null);
  
  // Get channels
  const debateChannelName = getDebateChannelName(debateId);
  const debateChannel = usePusherChannel(debateChannelName);
  
  // Store channel in ref for direct access
  useEffect(() => {
    if (debateChannel) {
      debateChannelRef.current = debateChannel;
      setIsConnected(true);
    } else {
      setIsConnected(false);
    }
  }, [debateChannel]);
  
  // Handle user-specific channel if user is authenticated
  const userChannelName = user ? getUserChannelName(user.id) : null;
  const userChannel = usePusherChannel(userChannelName || '');
  
  // Store user channel in ref
  useEffect(() => {
    if (userChannel && userChannelName) {
      userChannelRef.current = userChannel;
    }
  }, [userChannel, userChannelName]);
  
  // Handle new messages
  const handleNewMessage = useCallback((data) => {
    safeLogger.info('🔔 KID B RECEIVED: The tower sent me a message!', {
      event: 'message_received',
      dataId: data.id || data._id,
      dataContent: data.content?.substring(0, 30) + (data.content?.length > 30 ? '...' : ''),
      dataTeamId: data.teamId,
      dataSenderId: data.senderId,
      userId: user?.id,
      isMyMessage: data.senderId === user?.id,
      currentDebateId: currentDebate?.id,
      messageDebateId: data.debateId,
      isForCurrentDebate: currentDebate && currentDebate.id === data.debateId
    });
    
    // Only update if this is for the current debate we're viewing
    if (currentDebate && currentDebate.id === data.debateId) {
      safeLogger.info('🔔 PUSHER: Adding new message to current debate from Pusher event');
      
      // Skip refresh for any message that's still processing to preserve processing state
      if (data.isProcessing) {
        safeLogger.info('🔔 PUSHER: Skipping refresh for processing message to preserve processing state', {
          messageId: data.id,
          senderId: data.senderId,
          isOwnMessage: data.senderId === user?.id
        });
        
        // HYBRID: Wait for Pusher event first, then fallback polling after 15s
        if (data.senderId !== user?.id && data.id) {
          safeLogger.info('🔔 PUSHER: Received processing message from other user - waiting for analysis_complete event', {
            messageId: data.id,
            debateId: data.debateId
          });
          
          // Fallback: Start polling after 15 seconds if no Pusher event (serverless timeout protection)
          const timeoutId = setTimeout(() => {
            safeLogger.info('🔔 PUSHER: No Pusher event received within 15s, starting fallback polling');
            startFallbackPolling(data.debateId, data.id);
          }, 15000); // 15 second timeout
          
          // Store timeout ID to cancel if Pusher event arrives
          window.analysisTimeouts = window.analysisTimeouts || new Map();
          window.analysisTimeouts.set(data.id, timeoutId);
        }
      } else {
        // Only refresh for completed messages from other users
        if (data.senderId !== user?.id) {
          try {
            safeLogger.info('🔔 PUSHER: Refreshing for completed message from other user');
            if (fetchDebateById && currentDebate?.id) {
              setTimeout(() => {
                fetchDebateById(currentDebate.id, true)
                  .then(() => safeLogger.info('🔔 PUSHER: Successfully refreshed debate data'))
                  .catch(err => safeLogger.error('🔔 PUSHER: Error refreshing debate data:', err));
              }, 200);
            }
          } catch (error) {
            safeLogger.error('🔔 PUSHER: Error refreshing debate data:', error);
          }
        } else {
          safeLogger.info('🔔 PUSHER: Skipping refresh for own completed message');
        }
      }
      
      // Show toast notification - DISABLE to prevent duplicate toasts
      // Toast notifications are now handled by the main DebateContext only
      // if (data.flags && data.flags.length > 0) {
      //   // Only show for other people's messages to avoid duplicate notifications
      //   if (data.senderId !== user?.id) {
      //     toast.error('A message with flags was received!');
      //   }
      // } else {
      //   // Only show for other people's messages to avoid duplicate notifications
      //   if (data.senderId !== user?.id) {
      //     toast.success('New message received!');
      //   }
      // }
    } else {
      safeLogger.info('🔔 PUSHER: Message is not for current debate, ignoring', {
        currentDebateId: currentDebate?.id,
        messageDebateId: data.debateId
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchDebateById/startFallbackPolling stable
  }, [currentDebate, setCurrentDebate, user?.id]);
  
  // Handle shitmove detection events
  const handleShitmoveDetected = useCallback((data) => {
    safeLogger.info('Received shitmove_detected event via Pusher:', data);
    
    // Only update if this is for the current debate we're viewing
    if (currentDebate && currentDebate.id === data.debateId) {
      safeLogger.info('usePusherForDebateRoom: Updating message flags from Pusher event');
      
      // CRITICAL UPDATE: Force fetchDebateById to get the latest flags
      try {
        safeLogger.info('🔔 PUSHER: Received flag update - forcing fetch of latest debate data');
        if (fetchDebateById && currentDebate?.id) {
          // Reduce timeout to prevent excessive fetching
          setTimeout(() => {
            fetchDebateById(currentDebate.id, true)
              .then(() => safeLogger.info('🔔 PUSHER: Successfully refreshed debate data with flags'))
              .catch(err => safeLogger.error('🔔 PUSHER: Error refreshing debate data with flags:', err));
          }, 200); // Increased from 100ms to 200ms to reduce frequency
        } else {
          safeLogger.info('🔔 PUSHER: Cannot fetch latest debate data with flags - missing fetchDebateById or currentDebate.id');
        }
      } catch (error) {
        safeLogger.error('🔔 PUSHER: Error refreshing debate data with flags:', error);
      }

      // Show toast notification for shitmove - DISABLE to prevent duplicate toasts
      // toast.error('A logical fallacy was detected in a message!');
    }
  }, [currentDebate, fetchDebateById]);
  
  // Handle flag lifted events
  const handleFlagLifted = useCallback((data) => {
    safeLogger.info('Received flag_lifted event via Pusher:', data);
    
    // Only update if this is for the current debate we're viewing
    if (currentDebate && currentDebate.id === data.debateId) {
      safeLogger.info('usePusherForDebateRoom: Updating message flag from Pusher event');
      
      // CRITICAL UPDATE: Force fetchDebateById to get the latest lifted flags
      try {
        safeLogger.info('🔔 PUSHER: Received flag lifted update - forcing fetch of latest debate data');
        if (fetchDebateById && currentDebate?.id) {
          // Use a delay to avoid race conditions with other components
          setTimeout(() => {
            fetchDebateById(currentDebate.id, true)
              .then(() => safeLogger.info('🔔 PUSHER: Successfully refreshed debate data with lifted flag'))
              .catch(err => safeLogger.error('🔔 PUSHER: Error refreshing debate data with lifted flag:', err));
          }, 100); // Reduced timeout
        } else {
          safeLogger.info('🔔 PUSHER: Cannot fetch latest debate data with lifted flag - missing fetchDebateById or currentDebate.id');
        }
      } catch (error) {
        safeLogger.error('🔔 PUSHER: Error refreshing debate data with lifted flag:', error);
      }
      
      // If the event indicates all flags are lifted and includes a note, cache it locally for UI
      try {
        if (data.allFlagsLifted && data.flagsLiftedNote && data.messageId) {
          // We can't set liftedNotes here directly (state lives in DebateRoomPage),
          // but we can store a transient map on window for the page to read and merge.
          const key = String(data.messageId);
          window.__liftedFlagNotes = window.__liftedFlagNotes || {};
          window.__liftedFlagNotes[key] = {
            by: data.flagsLiftedNote.byUserId || 'Someone',
            at: data.flagsLiftedNote.at || new Date().toISOString(),
            flags: data.flagsLiftedNote.flags || [],
          };
        }
      } catch (e) {
        safeLogger.warn('Failed to cache lifted flag note from Pusher event:', e);
      }
      
      // Show success toast notification when a flag is lifted - DISABLE to prevent duplicate toasts
      // toast.success('A flag has been lifted!');
    }
  }, [currentDebate, fetchDebateById]);
  
  // Handle user typing events
  const handleUserTyping = useCallback((data) => {
    safeLogger.info('Received user_typing event:', data);
    
    if (data.debateId === debateId) {
      // Skip own typing events
      if (data.userId === user?.id) {
        return;
      }
      
      if (data.isTyping) {
        // Add typing user
        setTypingUsers(prev => {
          const exists = prev.some(u => u.userId === data.userId);
          if (exists) {
            return prev.map(u => u.userId === data.userId ? { ...u, timestamp: new Date() } : u);
          } else {
            return [...prev, { ...data, timestamp: new Date() }];
          }
        });
      } else {
        // Remove typing user
        setTypingUsers(prev => prev.filter(u => u.userId !== data.userId));
      }
    }
  }, [debateId, user?.id]);
  
  // Handle turn notifications
  const handleTurnNotification = useCallback((data) => {
    safeLogger.info('Received turn_notification event:', data);
    
    if (data.debateId === debateId) {
      // Update the current turn in the debate object
      if (typeof setCurrentDebate === 'function' && currentDebate) {
        setCurrentDebate(prevDebate => {
          if (!prevDebate) return prevDebate;
          
          return {
            ...prevDebate,
            currentTurn: data.teamId
          };
        });
      }
      
      // Show notification based on whether it's this user's team's turn
      if (data.teamId === teamId) {
        toast.success("It's your team's turn to respond!");
      } else {
        toast.info(`It's ${data.teamName || 'the other team'}'s turn.`);
      }
    }
  }, [debateId, teamId, currentDebate, setCurrentDebate]);
  
  // Handle analysis complete events (when AI analysis finishes)
  const handleAnalysisComplete = useCallback((data) => {
    safeLogger.info('✅ PUSHER EVENT: analysis_complete received in usePusherForDebateRoom:', {
      messageId: data.messageId,
      debateId: data.debateId,
      hasFlags: data.hasFlags,
      isProcessing: data.isProcessing,
      senderId: data.senderId,
      currentDebateId: currentDebate?.id,
      isForCurrentDebate: currentDebate && currentDebate.id === data.debateId
    });
    
    // Cancel fallback polling timeout if Pusher event arrives
    const timeoutId = window.analysisTimeouts?.get(data.messageId);
    if (timeoutId) {
      clearTimeout(timeoutId);
      window.analysisTimeouts.delete(data.messageId);
      safeLogger.info('🔔 PUSHER: Cancelled fallback polling - Pusher event received');
    }
    
    // Only update if this is for the current debate we're viewing
    if (currentDebate && currentDebate.id === data.debateId) {
      safeLogger.info('🔔 PUSHER: AI analysis completed for current debate - refreshing data');
      
      // Refresh the debate data to get the updated message with analysis results
      if (typeof fetchDebateById === 'function') {
        fetchDebateById(debateId, true).catch(err => {
          safeLogger.error('Error refreshing debate after analysis complete:', err);
        });
      }
      
      // Do not show a toast here; UI already shows flags clearly and DebateContext may show sender toast
    }
  }, [currentDebate, debateId, fetchDebateById]);

  // Handle debate update events
  const handleDebateUpdated = useCallback((data) => {
    safeLogger.info('Received debate_updated event:', data);
    
    if (data.debateId === debateId || data.id === debateId) {
      // Refresh the debate data
      if (typeof fetchDebateById === 'function') {
        fetchDebateById(debateId, true).catch(err => {
          safeLogger.error('Error refreshing debate after update:', err);
        });
      }
      
      // Show appropriate notification
      if (data.updateType) {
        switch (data.updateType) {
          case 'status_change':
            if (data.newStatus === 'completed') {
              toast.success('The debate has been completed!');
            } else {
              toast.info(`Debate status changed to: ${data.newStatus}`);
            }
            break;
          case 'winner_set':
            const isUserTeamWinner = data.winnerTeamId === teamId;
            if (isUserTeamWinner) {
              toast.success('Your team has been declared the winner!');
            } else {
              toast.info(`A winner has been declared: ${data.winnerName || 'See debate results'}`);
            }
            break;
          default:
            toast.info('The debate has been updated');
        }
      }
    }
  }, [debateId, teamId, fetchDebateById]);
  
  // Bind to Pusher events when the channel is available
  usePusherEvent(debateChannel, 'message_received', handleNewMessage);
  usePusherEvent(debateChannel, 'shitmove_detected', handleShitmoveDetected);
  usePusherEvent(debateChannel, 'analysis_complete', handleAnalysisComplete);
  usePusherEvent(debateChannel, 'flag_lifted', handleFlagLifted);
  usePusherEvent(debateChannel, 'user_typing', handleUserTyping);
  usePusherEvent(debateChannel, 'turn_notification', handleTurnNotification);
  usePusherEvent(debateChannel, 'debate_updated', handleDebateUpdated);
  
  // Cleanup typing users after inactivity
  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      const now = new Date();
      setTypingUsers(prev => {
        return prev.filter(user => {
          const elapsed = now.getTime() - user.timestamp.getTime();
          return elapsed < 5000; // Remove after 5 seconds of inactivity
        });
      });
    }, 1000);
    
    return () => {
      clearInterval(cleanupInterval);
    };
  }, []);
  
  // Send typing status
  const sendTypingStatus = useCallback(async (isTyping) => {
    if (!debateId || !teamId || !user?.id) return;
    
    try {
      // Send typing status via server API endpoint
      await sendPusherMessage(
        debateChannelName,
        'user_typing',
        {
          debateId,
          userId: user.id,
          teamId,
          username: user.username || 'A team member', 
          isTyping
        }
      );
    } catch (error) {
      safeLogger.error('Error sending typing status:', error);
    }
  }, [debateId, teamId, user, debateChannelName]);
  
  // Setup debate data refresh only when needed
  useEffect(() => {
    if (!debateId || !fetchDebateById) return;
    
    // Only do initial load - no periodic polling
    // Real-time updates come through Pusher events
    if (!isConnected) {
      safeLogger.info('Pusher not connected, fetching initial debate data');
    fetchDebateById(debateId, true).catch(err => {
      safeLogger.error('Error refreshing debate data:', err);
    });
    }
    
    // NO PERIODIC POLLING - Real-time updates handled by Pusher events
    // This eliminates the excessive /api/debates/:id calls every 2 minutes
    
  }, [debateId, fetchDebateById, isConnected]);
  
  // At the end of the event handlers section, add this additional debug event
  // Add a debug event to log all Pusher events for this debate channel
  usePusherEvent(debateChannel, '*', (data) => {
    safeLogger.info('🔔 PUSHER DEBUG: Pusher event (*) in debate channel:', {
      eventName: data?.event || 'unknown',
      channelName: debateChannelName,
      data: typeof data === 'object' ? {
        ...data,
        content: data.content?.substring(0, 30) + (data.content?.length > 30 ? '...' : '')
      } : data
    });
  });
  
  // Add additional debug logging for Pusher connection status - FIXED to prevent infinite loops
  useEffect(() => {
    safeLogger.info('🔔 PUSHER DEBUG: Connection status changed:', {
      isConnected,
      channelName: debateChannelName,
      hasDebateChannel: !!debateChannel,
      debateId,
      teamId,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps -- omit debateChannel to prevent loops
  }, [isConnected, debateChannelName, debateId, teamId]);
  
  // Return the hook's public API
  return {
    isConnected,
    debateChannel: debateChannelRef.current,
    userChannel: userChannelRef.current,
    typingUsers,
    sendTypingStatus
  };
};

export default usePusherForDebateRoom; 